import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Printer, FileSpreadsheet, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

// Column definitions matching PriceList.jsx
const COLUMN_CONFIG = {
  serial: { label: 'S.No', width: 'w-12' },
  product_name: { label: 'Product Name', width: 'flex-1' },
  packing: { label: 'Packing', width: 'w-24' },
  manufacturer: { label: 'Manufacturer', width: 'w-40' },
  category: { label: 'Category', width: 'w-32' },
  generic_name: { label: 'Generic Name', width: 'w-48' },
  pts_gst: { label: 'PTS + GST', width: 'w-28', isPrice: true },
  ptr_gst: { label: 'PTR + GST', width: 'w-28', isPrice: true },
  pte_usd: { label: 'PTE (USD $)', width: 'w-28', isPrice: true, isUSD: true },
  product_link: { label: 'Product Link', width: 'w-32', isLink: true },
  product_image: { label: 'Image', width: 'w-24', isImage: true }
};

const PriceListReport = () => {
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    const data = localStorage.getItem('priceListReportData');
    if (data) {
      const parsed = JSON.parse(data);
      setReportData(parsed);
    }
  }, []);

  const formatCurrency = (amount, isUSD = false) => {
    const num = parseFloat(amount) || 0;
    if (isUSD) {
      return `$ ${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `Rs. ${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handlePrint = () => {
    window.print();
  };

  // Get visible columns from selectedColumns or default
  const getVisibleColumns = () => {
    if (reportData?.selectedColumns && reportData.selectedColumns.length > 0) {
      return reportData.selectedColumns;
    }
    // Default columns
    return ['serial', 'product_name', 'packing', 'generic_name'];
  };

  const getCellValue = (item, colKey, idx) => {
    if (colKey === 'serial') return idx + 1;
    
    const config = COLUMN_CONFIG[colKey];
    const value = item[colKey];
    
    if (config?.isPrice) {
      return formatCurrency(value, config.isUSD);
    }
    if (config?.isLink && value) {
      return <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">View</a>;
    }
    if (config?.isImage && value) {
      return <img src={value} alt="" className="h-8 w-8 object-contain" />;
    }
    return value || '-';
  };

  const exportToPDF = () => {
    if (!reportData) return;

    const visibleCols = getVisibleColumns();
    const doc = new jsPDF(visibleCols.length > 5 ? 'landscape' : 'portrait');
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Price List', pageWidth / 2, 15, { align: 'center' });
    
    // Company info
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    if (reportData.company) {
      doc.text(reportData.company, pageWidth / 2, 22, { align: 'center' });
    }
    doc.text(`Generated: ${new Date().toLocaleDateString('en-IN')}`, pageWidth / 2, 28, { align: 'center' });
    doc.text(`Total Items: ${reportData.items?.length || 0}`, pageWidth / 2, 34, { align: 'center' });

    // Build table headers and data based on selected columns
    const headers = visibleCols.map(col => COLUMN_CONFIG[col]?.label || col);
    
    const tableData = (reportData.items || []).map((item, idx) => 
      visibleCols.map(col => {
        if (col === 'serial') return idx + 1;
        const config = COLUMN_CONFIG[col];
        const value = item[col];
        if (config?.isPrice) {
          const num = parseFloat(value) || 0;
          return config.isUSD ? `$ ${num.toFixed(2)}` : `Rs. ${num.toFixed(2)}`;
        }
        if (config?.isLink) return value ? 'Link' : '-';
        if (config?.isImage) return value ? 'Yes' : '-';
        return value || '-';
      })
    );

    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: 40,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [79, 70, 229], textColor: 255 },
      alternateRowStyles: { fillColor: [248, 250, 252] }
    });

    doc.save('price_list.pdf');
  };

  const exportToExcel = () => {
    if (!reportData) return;

    const visibleCols = getVisibleColumns();
    const headers = visibleCols.map(col => COLUMN_CONFIG[col]?.label || col);
    
    const worksheetData = [
      ['Price List'],
      [reportData.company || ''],
      [`Generated: ${new Date().toLocaleDateString('en-IN')}`],
      [],
      headers,
      ...(reportData.items || []).map((item, idx) => 
        visibleCols.map(col => {
          if (col === 'serial') return idx + 1;
          const config = COLUMN_CONFIG[col];
          const value = item[col];
          if (config?.isPrice) return parseFloat(value) || 0;
          return value || '';
        })
      )
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Price List');
    XLSX.writeFile(workbook, 'price_list.xlsx');
  };

  if (!reportData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-slate-700">No Report Data</h2>
          <p className="text-slate-500 mt-2">Please click "Print Price List" from the Price List page.</p>
        </div>
      </div>
    );
  }

  const visibleColumns = getVisibleColumns();

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Hidden when printing */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white p-3 sm:p-4 no-print">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <h1 className="text-lg sm:text-xl font-bold">Price List Report</h1>
            <div className="flex flex-wrap gap-2">
              <Button onClick={handlePrint} size="sm" className="bg-white/20 hover:bg-white/30 text-white text-xs sm:text-sm">
                <Printer className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Print
              </Button>
              <Button onClick={exportToPDF} size="sm" className="bg-red-500 hover:bg-red-600 text-white text-xs sm:text-sm">
                <FileText className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                PDF
              </Button>
              <Button onClick={exportToExcel} size="sm" className="bg-green-500 hover:bg-green-600 text-white text-xs sm:text-sm">
                <FileSpreadsheet className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Excel
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Report Content */}
      <div className="max-w-7xl mx-auto p-3 sm:p-6">
        {/* Report Header */}
        <div className="text-center mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Price List</h1>
          {reportData.company && (
            <p className="text-base sm:text-lg text-indigo-600 font-medium mt-1">{reportData.company}</p>
          )}
          <p className="text-xs sm:text-sm text-slate-500">Generated on {new Date().toLocaleDateString('en-IN')}</p>
          <p className="text-sm text-slate-600 font-medium mt-1 sm:mt-2">Total Items: {reportData.items?.length || 0}</p>
        </div>

        {/* Scrollable Table for all screen sizes */}
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full min-w-[800px]">
            <thead className="bg-indigo-600 text-white">
              <tr>
                {visibleColumns.map(col => (
                  <th key={col} className={`px-3 py-3 text-xs font-semibold uppercase whitespace-nowrap ${
                    COLUMN_CONFIG[col]?.isPrice ? 'text-right' : 
                    col === 'serial' ? 'text-center' : 'text-left'
                  }`}>
                    {COLUMN_CONFIG[col]?.label || col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {(reportData.items || []).map((item, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  {visibleColumns.map(col => (
                    <td key={col} className={`px-3 py-2 text-sm whitespace-nowrap ${
                      COLUMN_CONFIG[col]?.isPrice ? 'text-right font-medium text-indigo-600' : 
                      col === 'serial' ? 'text-center text-slate-500' : 
                      col === 'product_name' ? 'font-medium text-slate-800' : 'text-slate-600'
                    }`}>
                      {getCellValue(item, col, idx)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-slate-500 text-sm">
          <p>Price List Report - Stock Management System</p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
};

export default PriceListReport;
